/**
 * API Service
 *
 * HTTP client for communicating with the backend API.
 * Uses httpOnly cookies for authentication (sent automatically via
 * credentials: 'include') and adds CSRF token on state-changing methods.
 *
 * Professional behaviors this client provides:
 *
 *   1. GET request coalescing — if the same GET endpoint is already
 *      in-flight, additional callers share the same underlying fetch
 *      (dedup by URL). This absorbs React StrictMode double-mount and
 *      rapid re-renders without sending duplicate network requests,
 *      and keeps the backend throttler happy.
 *
 *   2. 429 handling with Retry-After — when the server responds with
 *      Too Many Requests, we wait the server-suggested interval (or a
 *      bounded exponential backoff if absent) and retry once before
 *      surfacing the error. Protects the UI from flaking during bursts.
 *
 *   3. 401 refresh+retry — expired access tokens trigger a single
 *      silent refresh via the TokenRefreshQueue (shared across concurrent
 *      401s), then the original request is retried exactly once.
 *
 *   4. Per-caller AbortSignal — the shared fetch keeps running, but
 *      individual callers can reject early by aborting their signal.
 */

import { tokenService } from './token.service';

// ============================================================================
// ERROR CLASSES
// ============================================================================

/**
 * Thrown when the browser couldn't reach the backend (fetch TypeError,
 * DNS failure, offline, JSON parse failure on empty body). Distinct
 * from `ServerError` so UI can render a "check connection" screen
 * instead of falling through to empty/not-found paths.
 */
export class NetworkError extends Error {
  constructor(message = 'Error de conexión. Verifica tu internet e intenta de nuevo.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Thrown when the backend returned 5xx. Server reachable but broken.
 * Paired with `NetworkError` so the UI can render a
 * "servidor no disponible" screen instead of the empty path.
 */
export class ServerError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ServerError';
    this.status = status;
  }
}

/**
 * Thrown when the backend answered 4xx. Keeps the HTTP status so callers can
 * tell "no existe" (404) from "no tienes permiso" (403) or a validation error.
 *
 * Antes el cliente lanzaba un `Error` sin más, y los dos sitios que preguntaban
 * por el estado —`commissionService` («sin comisión configurada» = 404) y la
 * ficha del cliente («este cliente no existe»)— nunca lo encontraban: el 404
 * se trataba como cualquier otro fallo. Sigue siendo un `Error` con el mismo
 * `message`, así que quien sólo lee el mensaje no nota la diferencia.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Estado HTTP de un error del cliente, si lo trae. */
export function apiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError || err instanceof ServerError) return err.status;
  return undefined;
}

/**
 * Classify an unknown error. Returns `null` for anything that isn't a
 * network/server problem (validation, 4xx, abort, etc.).
 */
export function classifyApiError(
  err: unknown,
): 'network' | 'server' | null {
  if (err instanceof NetworkError) return 'network';
  if (err instanceof ServerError) return 'server';
  return null;
}

// ============================================================================
// TYPES
// ============================================================================

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean;
  /**
   * Idempotency key to send on mutating requests (POST/PUT/PATCH/DELETE).
   * When omitted, a fresh UUID is generated per logical call; if the same
   * RequestConfig is reused (e.g. after a 401 → refresh retry) the same
   * key is preserved so the backend replays the cached response instead
   * of re-running the handler.
   */
  idempotencyKey?: string;
  /**
   * Internal: flag set after a 401 refresh+retry to avoid infinite loops
   * when the retry also 401s. Don't pass manually.
   */
  _retried?: boolean;
  /**
   * Internal: flag set after a 429 backoff+retry to bound attempts. Don't
   * pass manually.
   */
  _throttleRetried?: boolean;
  /**
   * Internal: if true, bypass GET coalescing. Used by 401 retries and
   * other internal reissues that must not be folded into the original
   * in-flight promise.
   */
  _bypassCoalesce?: boolean;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  meta?: {
    timestamp: string;
    path: string;
    version: string;
  };
  error?: {
    message: string;
    code?: string;
  };
}

/**
 * Evento publicado cuando una mutación (POST/PUT/PATCH/DELETE) termina
 * con respuesta 2xx. Lo usan vistas que muestran estado derivado de
 * varios endpoints para refrescar sin polling.
 */
export interface MutationEvent {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  status: number;
}

type MutationListener = (event: MutationEvent) => void;

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Máximo tiempo que esperamos tras un 429 antes de reintentar. Si el
 * backend manda Retry-After más grande, lo respetamos (hasta este cap).
 */
const MAX_THROTTLE_WAIT_MS = 5_000;

/**
 * Default backoff cuando el 429 viene sin Retry-After. Pequeño para no
 * congelar el UI; si vuelve a fallar, el error sube al caller.
 */
const DEFAULT_THROTTLE_WAIT_MS = 1_000;

// ============================================================================
// API CLIENT
// ============================================================================

/**
 * Tipo de fallo a nivel de transporte/servidor — el caller decide qué
 * UI mostrar (banner global, retry, etc.).
 */
export type FetchErrorKind = 'network' | 'server';

/** Listener que recibe cada error de fetch clasificado. */
export type FetchErrorListener = (kind: FetchErrorKind) => void;

class ApiClient {
  private baseURL: string;
  private mutationListeners = new Set<MutationListener>();
  /**
   * Listeners que se disparan en CADA `NetworkError`/`ServerError` del
   * cliente HTTP. El BackendStatusContext usa esto para decidir si
   * mostrar el banner global. NO se dispara para 4xx (auth, validación,
   * not-found, etc.) — esos no son outage del backend.
   */
  private fetchErrorListeners = new Set<FetchErrorListener>();
  /**
   * Marca el último éxito de un fetch para que el context pueda resetear
   * el contador de errores cuando el backend vuelve sin necesidad de un
   * ping explícito a /health. Cualquier 2xx baja el banner.
   */
  private fetchSuccessListeners = new Set<() => void>();

  /**
   * In-flight GET promises keyed by endpoint. Second caller to the same
   * endpoint joins the existing promise instead of firing a new fetch.
   * Limpia el entry cuando la promise resuelve/rechaza.
   */
  private inflightGets = new Map<string, Promise<ApiResponse<unknown>>>();

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Suscribe un listener que se dispara cada vez que una mutación 2xx
   * termina. Retorna función para desuscribir.
   */
  onMutation(listener: MutationListener): () => void {
    this.mutationListeners.add(listener);
    return () => {
      this.mutationListeners.delete(listener);
    };
  }

  /**
   * Anuncia una mutación hecha **fuera** de este cliente.
   *
   * Existe para las peticiones que no pueden pasar por aquí: las de
   * `multipart/form-data`, que van con `fetch` crudo porque el cliente
   * serializa a JSON. Sin esto no emitían nada, y quien escucha el bus se
   * quedaba sin enterarse.
   *
   * El síntoma era concreto: al crear una entidad bancaria con su logo, la
   * lista de requisitos del país tardaba en marcarla — sólo se enteraba al
   * volver el foco a la ventana, que es la red de seguridad, no el camino.
   */
  notifyExternalMutation(event: MutationEvent) {
    this.emitMutation(event);
  }

  private emitMutation(event: MutationEvent) {
    this.mutationListeners.forEach((fn) => {
      try {
        fn(event);
      } catch {
        /* swallow: listeners no deben afectar el flujo del request */
      }
    });
  }

  /**
   * Suscribe un listener para errores de transporte/servidor (NetworkError /
   * ServerError). Retorna función para desuscribir. Útil para mostrar un
   * banner global cuando el backend no responde.
   */
  onFetchError(listener: FetchErrorListener): () => void {
    this.fetchErrorListeners.add(listener);
    return () => {
      this.fetchErrorListeners.delete(listener);
    };
  }

  private emitFetchError(kind: FetchErrorKind) {
    this.fetchErrorListeners.forEach((fn) => {
      try {
        fn(kind);
      } catch {
        /* swallow */
      }
    });
  }

  /**
   * Suscribe un listener que se dispara en cada respuesta 2xx — sirve
   * para que el context de estado del backend resetee su contador.
   */
  onFetchSuccess(listener: () => void): () => void {
    this.fetchSuccessListeners.add(listener);
    return () => {
      this.fetchSuccessListeners.delete(listener);
    };
  }

  private emitFetchSuccess() {
    this.fetchSuccessListeners.forEach((fn) => {
      try {
        fn();
      } catch {
        /* swallow */
      }
    });
  }

  /**
   * Refresh via authService → tokenRefreshQueue (dynamic import para
   * evitar ciclo con auth.service).
   */
  private async refreshAccessToken(): Promise<void> {
    const { authService } = await import('./auth.service');
    await authService.refreshAccessToken();
  }

  /**
   * Punto de entrada público. GETs se coalescen por endpoint; el resto
   * de métodos van directo a `doRequest`.
   *
   * El `signal` del caller NO se propaga al fetch subyacente cuando hay
   * coalescing — si lo hiciéramos, un caller que aborta rompería la
   * request para todos los demás joiners. En su lugar, exponemos un
   * wrapper que rechaza localmente con AbortError cuando el signal del
   * caller se dispara, dejando que el fetch compartido siga para los
   * demás.
   */
  async request<T>(
    endpoint: string,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    const method = (config.method || 'GET').toUpperCase();
    const coalesceable =
      method === 'GET' && !config._bypassCoalesce && !config._retried;

    if (!coalesceable) {
      return this.doRequest<T>(endpoint, config);
    }

    const key = endpoint;
    let shared = this.inflightGets.get(key);
    if (!shared) {
      // Fire the real request WITHOUT the caller's signal — otherwise
      // the first caller unmounting (StrictMode cleanup) would abort
      // the fetch for all subsequent joiners.
      const { signal: _ignored, ...configNoSignal } = config;
      void _ignored;
      shared = this.doRequest<unknown>(endpoint, configNoSignal);
      this.inflightGets.set(key, shared);
      // Siempre limpiamos el entry cuando termina, sin importar éxito
      // o fallo — si cacheáramos el error, el siguiente mount nunca
      // se recuperaría.
      // `.finally()` devuelve una promesa NUEVA que también rechaza. Sin el
      // `catch` de abajo, cualquier GET coalescido que falle deja una rejection
      // que nadie maneja: en el navegador sale como «Uncaught (in promise)» y
      // en Vitest tumba el proceso entero con código 1 aunque no falle ni un
      // test. El error de verdad sigue viajando en `shared`, que es la promesa
      // que reciben los llamadores.
      void shared
        .finally(() => {
          if (this.inflightGets.get(key) === shared) {
            this.inflightGets.delete(key);
          }
        })
        .catch(() => undefined);
    }

    const callerSignal = config.signal;
    if (!callerSignal) return shared as Promise<ApiResponse<T>>;

    // Caller supplied an abort signal: race it against the shared
    // promise so they can cancel their await without affecting other
    // joiners.
    return new Promise<ApiResponse<T>>((resolve, reject) => {
      if (callerSignal.aborted) {
        reject(
          (callerSignal as AbortSignal & { reason?: unknown }).reason ??
            new DOMException('Aborted', 'AbortError'),
        );
        return;
      }
      const onAbort = () => {
        reject(
          (callerSignal as AbortSignal & { reason?: unknown }).reason ??
            new DOMException('Aborted', 'AbortError'),
        );
      };
      callerSignal.addEventListener('abort', onAbort, { once: true });
      (shared as Promise<ApiResponse<T>>).then(
        (v) => {
          callerSignal.removeEventListener('abort', onAbort);
          resolve(v);
        },
        (e) => {
          callerSignal.removeEventListener('abort', onAbort);
          reject(e);
        },
      );
    });
  }

  /**
   * Performs the actual fetch + parsing. Handles:
   *   - auth headers (device ID, CSRF, Idempotency-Key)
   *   - 204 No Content
   *   - 401 → refresh + one-shot retry
   *   - 429 → Retry-After backoff + one-shot retry
   *   - Mutation event broadcast
   */
  private async doRequest<T>(
    endpoint: string,
    config: RequestConfig,
  ): Promise<ApiResponse<T>> {
    const { requiresAuth = true, headers = {}, signal, ...restConfig } = config;
    const url = `${this.baseURL}${endpoint}`;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-platform': 'DASHBOARD_ADMIN',
      ...(headers as Record<string, string>),
    };

    if (requiresAuth) {
      const deviceId = tokenService.getExistingDeviceId();
      if (deviceId) {
        requestHeaders['x-device-id'] = deviceId;
      }
    }

    const method = (restConfig.method || 'GET').toUpperCase();
    if (STATE_CHANGING_METHODS.has(method)) {
      const csrfToken = tokenService.getCsrfToken();
      if (csrfToken) {
        requestHeaders['X-CSRF-Token'] = csrfToken;
      }

      if (!config.idempotencyKey) {
        config.idempotencyKey = crypto.randomUUID();
      }
      requestHeaders['Idempotency-Key'] = config.idempotencyKey;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...restConfig,
        headers: requestHeaders,
        credentials: 'include',
        signal,
      });
    } catch (err) {
      // AbortError se rethrowea tal cual para que `isAbortError` lo
      // detecte. Fallos de red (TypeError de fetch, timeout) se
      // clasifican como `NetworkError` para que la UI pueda renderizar
      // un estado de conexión rather than morph into a silent empty.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      this.emitFetchError('network');
      if (err instanceof Error) {
        throw new NetworkError(err.message);
      }
      throw new NetworkError();
    }

    // 401 → silent refresh + single retry.
    if (response.status === 401 && requiresAuth && !config._retried) {
      await this.refreshAccessToken();
      return this.doRequest<T>(endpoint, {
        ...config,
        _retried: true,
        _bypassCoalesce: true,
      });
    }

    // 429 → backoff (Retry-After si el server lo manda, si no default)
    // y un único reintento. Protege al UI de bursts momentáneos.
    if (response.status === 429 && !config._throttleRetried) {
      const waitMs = this.parseRetryAfter(response.headers.get('retry-after'));
      await this.sleep(waitMs, signal);
      return this.doRequest<T>(endpoint, {
        ...config,
        _throttleRetried: true,
        _bypassCoalesce: true,
      });
    }

    // 204 No Content
    if (response.status === 204) {
      this.emitFetchSuccess();
      if (STATE_CHANGING_METHODS.has(method)) {
        this.emitMutation({
          method: method as MutationEvent['method'],
          endpoint,
          status: 204,
        });
      }
      return {
        success: true,
        data: undefined as T,
        meta: {
          timestamp: new Date().toISOString(),
          path: endpoint,
          version: 'v1',
        },
      };
    }

    let data: ApiResponse<T>;
    try {
      data = await response.json();
    } catch {
      if (response.status >= 500) {
        this.emitFetchError('server');
        throw new ServerError(
          response.status,
          this.getDefaultErrorMessage(response.status),
        );
      }
      this.emitFetchError('network');
      throw new NetworkError('Error de conexión. Verifica tu internet.');
    }

    if (!response.ok) {
      const errorMessage =
        data.error?.message ||
        (data.message as string) ||
        this.getDefaultErrorMessage(response.status);
      if (response.status >= 500) {
        this.emitFetchError('server');
        throw new ServerError(response.status, errorMessage);
      }
      // 4xx (auth, validación, not-found): NO es outage — no emitimos.
      throw new ApiError(response.status, errorMessage, data.error?.code);
    }

    // Cualquier 2xx baja el banner de error global automáticamente.
    this.emitFetchSuccess();

    if (STATE_CHANGING_METHODS.has(method)) {
      this.emitMutation({
        method: method as MutationEvent['method'],
        endpoint,
        status: response.status,
      });
    }

    return data;
  }

  /**
   * Parsea el header Retry-After (segundos, o HTTP-date). Cap al máximo
   * configurado para no bloquear UI indefinidamente. Default si viene
   * vacío o no parseable.
   */
  private parseRetryAfter(header: string | null): number {
    if (!header) return DEFAULT_THROTTLE_WAIT_MS;
    const asSeconds = Number(header);
    if (Number.isFinite(asSeconds) && asSeconds > 0) {
      return Math.min(asSeconds * 1000, MAX_THROTTLE_WAIT_MS);
    }
    const asDate = Date.parse(header);
    if (Number.isFinite(asDate)) {
      const delta = asDate - Date.now();
      if (delta > 0) return Math.min(delta, MAX_THROTTLE_WAIT_MS);
    }
    return DEFAULT_THROTTLE_WAIT_MS;
  }

  /**
   * Sleep cancelable via AbortSignal. Si el signal aborta durante el
   * sleep, la promise rechaza con AbortError inmediatamente.
   */
  private sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const timer = setTimeout(() => {
        if (signal) signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  private getDefaultErrorMessage(status: number): string {
    const messages: Record<number, string> = {
      400: 'Datos inválidos. Verifica la información.',
      401: 'Credenciales incorrectas.',
      403: 'No tienes permiso para acceder.',
      404: 'Recurso no encontrado.',
      409: 'Acción duplicada o en proceso. Espera un momento e intenta de nuevo.',
      422: 'Los datos enviados no son válidos.',
      429: 'Demasiados intentos. Espera un momento.',
      500: 'Error del servidor. Intenta más tarde.',
      502: 'Servidor no disponible.',
      503: 'Servicio en mantenimiento.',
    };
    return messages[status] || `Error ${status}. Intenta de nuevo.`;
  }

  async get<T>(
    endpoint: string,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(
    endpoint: string,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const apiClient = new ApiClient(API_BASE_URL);
export type { ApiResponse, RequestConfig };
